import { supabase } from '@/utils/supabase';

export type PayoutPaymentBatchSource = 'manual' | 'legacy_backfill' | 'historical_repair';

export interface PayoutPaymentAllocation {
  id: string;
  batchId: string;
  payoutId: string | null;
  payoutIdSnapshot: string;
  bankerReconId: string | null;
  loanNumber: string;
  borrowerName: string | null;
  recipientId: string;
  payoutType: string;
  payoutSequence: number;
  amount: number;
}

export interface PayoutPaymentBatch {
  id: string;
  recipientId: string;
  recipientName: string;
  checkNumber: string;
  paymentDate: string;
  totalAmount: number;
  memo: string | null;
  source: PayoutPaymentBatchSource;
  historyComplete: boolean;
  createdByName: string | null;
  createdAt: string;
  voidedAt: string | null;
  voidedByName: string | null;
  voidReason: string | null;
  paymentEmailSentAt: string | null;
  paymentEmailSentByName: string | null;
  paymentEmailRecipient: string | null;
  paymentEmailResendId: string | null;
  allocations: PayoutPaymentAllocation[];
}

interface RawPaymentAllocation {
  id: string;
  batch_id: string;
  payout_id: string | null;
  payout_id_snapshot: string;
  banker_recon_id: string | null;
  loan_number: string;
  borrower_name_snapshot: string | null;
  recipient_id: string;
  payout_type: string;
  payout_sequence: number;
  amount: number | string;
}

interface RawPaymentBatch {
  id: string;
  recipient_id: string;
  recipient_name_snapshot: string;
  check_number: string;
  payment_date: string;
  total_amount: number | string;
  memo: string | null;
  source: PayoutPaymentBatchSource;
  history_complete: boolean;
  created_by_name_snapshot: string | null;
  created_at: string;
  voided_at: string | null;
  voided_by_name_snapshot: string | null;
  void_reason: string | null;
  payment_email_sent_at: string | null;
  payment_email_sent_by_name_snapshot: string | null;
  payment_email_recipient_snapshot: string | null;
  payment_email_resend_id: string | null;
  allocations?: RawPaymentAllocation[] | null;
}

const PAYMENT_BATCH_SELECT = `
  id,
  recipient_id,
  recipient_name_snapshot,
  check_number,
  payment_date,
  total_amount,
  memo,
  source,
  history_complete,
  created_by_name_snapshot,
  created_at,
  voided_at,
  voided_by_name_snapshot,
  void_reason,
  payment_email_sent_at,
  payment_email_sent_by_name_snapshot,
  payment_email_recipient_snapshot,
  payment_email_resend_id,
  allocations:payout_payment_allocations (
    id,
    batch_id,
    payout_id,
    payout_id_snapshot,
    banker_recon_id,
    loan_number,
    borrower_name_snapshot,
    recipient_id,
    payout_type,
    payout_sequence,
    amount
  )
`;

const FILTERED_PAYMENT_BATCH_SELECT = PAYMENT_BATCH_SELECT.replace(
  'allocations:payout_payment_allocations (',
  'allocations:payout_payment_allocations!inner ('
);

function mapAllocation(raw: RawPaymentAllocation): PayoutPaymentAllocation {
  return {
    id: raw.id,
    batchId: raw.batch_id,
    payoutId: raw.payout_id,
    payoutIdSnapshot: raw.payout_id_snapshot,
    bankerReconId: raw.banker_recon_id,
    loanNumber: raw.loan_number,
    borrowerName: raw.borrower_name_snapshot,
    recipientId: raw.recipient_id,
    payoutType: raw.payout_type,
    payoutSequence: raw.payout_sequence,
    amount: Number(raw.amount)
  };
}

export function mapPaymentBatch(raw: RawPaymentBatch): PayoutPaymentBatch {
  return {
    id: raw.id,
    recipientId: raw.recipient_id,
    recipientName: raw.recipient_name_snapshot,
    checkNumber: raw.check_number,
    paymentDate: raw.payment_date,
    totalAmount: Number(raw.total_amount),
    memo: raw.memo,
    source: raw.source,
    historyComplete: raw.history_complete,
    createdByName: raw.created_by_name_snapshot,
    createdAt: raw.created_at,
    voidedAt: raw.voided_at,
    voidedByName: raw.voided_by_name_snapshot,
    voidReason: raw.void_reason,
    paymentEmailSentAt: raw.payment_email_sent_at,
    paymentEmailSentByName: raw.payment_email_sent_by_name_snapshot,
    paymentEmailRecipient: raw.payment_email_recipient_snapshot,
    paymentEmailResendId: raw.payment_email_resend_id,
    allocations: (raw.allocations || []).map(mapAllocation)
  };
}

export async function getPayoutPaymentBatches(): Promise<PayoutPaymentBatch[]> {
  const { data, error } = await supabase
    .from('payout_payment_batches')
    .select(PAYMENT_BATCH_SELECT)
    .order('payment_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return ((data || []) as unknown as RawPaymentBatch[]).map(mapPaymentBatch);
}

export async function getPayoutPaymentBatchesForPayoutIds(
  payoutIds: string[]
): Promise<PayoutPaymentBatch[]> {
  if (payoutIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from('payout_payment_batches')
    .select(FILTERED_PAYMENT_BATCH_SELECT)
    .in('allocations.payout_id', payoutIds)
    .order('payment_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return ((data || []) as unknown as RawPaymentBatch[]).map(mapPaymentBatch);
}

export interface RecordPaymentAllocationInput {
  payoutId: string;
  amount: number;
}

export interface RecordedPayoutPaymentAllocation {
  payoutId: string;
  /** Server settlement status includes active non-cash recapture offsets. */
  status: string;
  /** Physical cash only. Callers refresh payouts for the full settlement shape. */
  paymentAmount: number;
}

export interface RecordedPayoutPaymentBatch {
  batchId: string;
  allocations: RecordedPayoutPaymentAllocation[];
}

export async function recordPayoutPaymentBatch(input: {
  checkNumber: string;
  paymentDate: string;
  memo?: string;
  idempotencyKey: string;
  allocations: RecordPaymentAllocationInput[];
}): Promise<RecordedPayoutPaymentBatch> {
  const { data, error } = await supabase.rpc('record_payout_payment_batch', {
    p_items: input.allocations.map((allocation) => ({
      payout_id: allocation.payoutId,
      amount: allocation.amount
    })),
    p_check_number: input.checkNumber,
    p_payment_date: input.paymentDate,
    p_memo: input.memo?.trim() || null,
    p_idempotency_key: input.idempotencyKey
  });

  if (error) {
    throw error;
  }
  if (!data || data.length !== input.allocations.length) {
    throw new Error(
      `Expected ${input.allocations.length} payment updates, received ${data?.length ?? 0}. Refresh and verify before retrying.`
    );
  }

  const rows = data as Array<{
    batch_id: string;
    payout_id: string;
    status: string;
    payment_amount: number | string;
  }>;
  const batchId = rows[0]?.batch_id;
  if (!batchId || rows.some((row) => row.batch_id !== batchId)) {
    throw new Error('Payment was recorded, but its physical-check batch could not be identified. Refresh before retrying.');
  }

  return {
    batchId,
    allocations: rows.map((row) => ({
      payoutId: row.payout_id,
      status: row.status,
      paymentAmount: Number(row.payment_amount)
    }))
  };
}

export interface PayoutPaymentEmailSendResult {
  emailId: string | null;
  sentAt: string;
  recipientEmail: string | null;
  auditLogged: boolean;
}

export async function sendPayoutPaymentConfirmation(
  batchId: string
): Promise<PayoutPaymentEmailSendResult> {
  const { data, error } = await supabase.functions.invoke('send-notification', {
    body: {
      notificationType: 'payout_payment_confirmation',
      data: { paymentBatchId: batchId }
    }
  });

  if (error) {
    throw error;
  }
  if (!data?.success) {
    throw new Error(data?.error || 'Payment email could not be sent');
  }

  return {
    emailId: typeof data.emailId === 'string' ? data.emailId : null,
    sentAt: typeof data.sentAt === 'string' ? data.sentAt : new Date().toISOString(),
    recipientEmail: Array.isArray(data.recipientEmails) && typeof data.recipientEmails[0] === 'string'
      ? data.recipientEmails[0]
      : null,
    auditLogged: data.auditLogged !== false
  };
}

export async function voidPayoutPaymentBatch(batchId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('void_payout_payment_batch', {
    p_batch_id: batchId,
    p_reason: reason.trim()
  });

  if (error) {
    throw error;
  }
}

export async function relinkPayoutPaymentAllocations(bankerReconId: string): Promise<number> {
  const { data, error } = await supabase.rpc('relink_payout_payment_allocations', {
    p_banker_recon_id: bankerReconId
  });

  if (error) {
    throw error;
  }

  return Number(data || 0);
}
